import { uniq } from 'lodash-es'
import { normalizeStringWithFolding } from '#app/lib/strings'
import { objectKeys, objectValues } from '#app/lib/utils'
import type { SerializedEntity, SerializedInvEntity, SerializedWdEntity } from '#entities/lib/entities'
import type { LabelsParts } from '#entities/lib/get_works_merge_candidates'
import { getAuthorWorks, type WorkEntityExtras } from '#entities/lib/types/author_alt'
import { addWorksImagesAndAuthors } from '#entities/lib/types/work_alt'
import type { InvEntityUri, TermLanguageCode } from '#server/types/entity'
import { getCurrentLang } from '#user/lib/i18n'

export type DeduplicationWorkEntity = (SerializedInvEntity | SerializedWdEntity) & WorkEntityExtras & {
  _matchable?: string[]
  _labelsParts?: LabelsParts
  bestMatchScore?: Record<InvEntityUri, number>
  series?: SerializedEntity[]
  works?: SerializedEntity[]
}

export function select (entity: DeduplicationWorkEntity, from: DeduplicationWorkEntity, to: DeduplicationWorkEntity) {
  if (!from) {
    if (to === entity) to = null
    if (entity.prefix === 'wd') to = entity
    else from = entity
  } else if (!to) {
    if (from === entity) from = null
    to = entity
  } else {
    if (entity.prefix === 'wd') {
      from = null
      to = entity
    } else {
      from = entity
      to = null
    }
  }
  return { from, to }
}

export function getFilterPattern (text: string) {
  if (!text || text.trim().length === 0) return ''

  // Using a regex allows to accept OR operators in the form of `|`
  let pattern
  try {
    const normalized = normalizeStringWithFolding(text)
    if (text === normalized) {
      pattern = new RegExp(`(${text})`, 'i')
    } else {
      pattern = new RegExp(`(${text}|${normalizeStringWithFolding(text)})`, 'i')
    }
  } catch (err) {
    if (err.name !== 'SyntaxError') throw err
    const escapedText = text.replace(/(\W)/g, '\\$1')
    pattern = new RegExp(escapedText, 'i')
  }

  return pattern
}

export function getEntityFilter (text: string, pattern: RegExp) {
  if (text.length === 0) return () => true
  else return (entity: DeduplicationWorkEntity) => anyLabelOrAliasMatch(entity, pattern)
}

function anyLabelOrAliasMatch (entity: DeduplicationWorkEntity, pattern: RegExp) {
  entity._matchable ??= getEntityLabelsAndAliases(entity)
  return entity._matchable.some(term => term.match(pattern))
}

function getEntityLabelsAndAliases (entity: DeduplicationWorkEntity) {
  const labels = objectValues(entity.labels)
  const aliases = objectValues(entity.aliases || []).flat()
  const terms = uniq(labels.concat(aliases))
  return uniq([ ...terms, ...terms.map(normalizeStringWithFolding) ])
}

type TermOrigin = `label.${TermLanguageCode}` | `alias.${TermLanguageCode}`
type Terms = Partial<Record<string, TermOrigin[]>>
export interface TermEntry {
  term: string
  normalized: string
  getMatchParts: (filterPattern: RegExp) => string[]
  origins: TermOrigin[]
}

export function getAggregatedLabelsAndAliases (entity: DeduplicationWorkEntity): TermEntry[] {
  const terms: Terms = {}
  for (const lang of objectKeys(entity.labels)) {
    const label = entity.labels[lang]
    terms[label] ??= []
    terms[label].push(`label.${lang}`)
  }
  if (entity.aliases) {
    for (const lang of objectKeys(entity.aliases)) {
      for (const alias of entity.aliases[lang]) {
        terms[alias] ??= []
        terms[alias].push(`alias.${lang}`)
      }
    }
  }

  return objectKeys(terms)
  .map((term: string) => {
    const normalized = normalizeStringWithFolding(term)
    return {
      term,
      normalized,
      getMatchParts: getMatchingParts(term, normalized),
      origins: terms[term],
    }
  })
  .sort((a, b) => {
    if (a.origins.length === b.origins.length) {
      return getTermPreferredOriginsCount(b) - getTermPreferredOriginsCount(a)
    } else {
      return b.origins.length - a.origins.length
    }
  })
}

const getMatchingParts = (term: string, normalized: string) => (filterPattern: RegExp) => {
  const termMatching = term.match(filterPattern)?.[0]
  if (termMatching) return getMatchingPartsArray(term, termMatching)
  const normalizedMatching = normalized.match(filterPattern)?.[0]
  if (normalizedMatching) return getMatchingPartsArray(normalized, normalizedMatching)
}

function getMatchingPartsArray (term: string, matching: string) {
  const parts = term.split(matching).map(partAround => [ partAround, matching ])
  return parts.flat().slice(0, -1)
}

const getTermPreferredOriginsCount = ({ origins }: TermEntry) => origins.filter(isPreferredOrigin).length

function isPreferredOrigin (origin: TermOrigin) {
  const lang = origin.split('.')[1] as TermLanguageCode
  return (lang === getCurrentLang() || lang === 'en')
}

export async function getAuthorWorksWithImagesAndCoauthors (author: SerializedEntity) {
  const { uri } = author
  const works = await getAuthorWorks({ uri })
  await addWorksImagesAndAuthors(works)
  works.forEach(work => {
    work.coauthors = work.authors.filter(coauthor => coauthor.uri !== author.uri)
  })
  return works
}

export interface WorksByPrefix {
  wd: DeduplicationWorkEntity[]
  inv: DeduplicationWorkEntity[]
}
export function spreadByPrefix (works: DeduplicationWorkEntity[]) {
  const worksByPrefix: WorksByPrefix = { wd: [], inv: [] }
  works.forEach(work => {
    worksByPrefix[work.prefix].push(work)
  })
  return worksByPrefix
}

export function sortEntitiesAlphabetically (a: DeduplicationWorkEntity, b: DeduplicationWorkEntity) {
  const labelA = a.label || ''
  const labelB = b.label || ''
  if (labelA.toLowerCase() > labelB.toLowerCase()) return 1
  else return -1
}
